import { useLayoutEffect, useState } from "react";
import { InvitesHttpService } from "@/modules/candidate/services/InvitesHttpService";
import { LanguagesIsoEnum } from "@/modules/assessments/types/test.interface";
import type { GetDemographicsResponse } from "./demographics.types";
import { takePreloadedDemographics } from "./demographicsPreload";

interface UseDemographicsOptionsArgs {
  inviteId?: number;
  demographicsAlreadyCollected: boolean;
  locale?: LanguagesIsoEnum;
}

/**
 * Loads the demographic questions for an invite, with question and choice labels already resolved by
 * the API to the requested locale.
 *
 * @param {UseDemographicsOptionsArgs} args - Hook arguments.
 * @param {number} [args.inviteId] - Invite whose questions to load; nothing is requested without it.
 * @param {boolean} args.demographicsAlreadyCollected - Skips the request once responses exist.
 * @param {LanguagesIsoEnum} [args.locale] - Locale to resolve the labels to. Re-requests when it changes;
 * omitted, the API uses the invite's saved locale and serves en-US while the invite has none.
 * @returns {{ questions: DemographicQuestion[]; isLoading: boolean; error: unknown }} Loaded questions with request state.
 */
export function useDemographicsOptions({ inviteId, demographicsAlreadyCollected, locale }: UseDemographicsOptionsArgs) {
  const [data, setData] = useState<GetDemographicsResponse | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<unknown>(null);

  useLayoutEffect(() => {
    if (inviteId == null || demographicsAlreadyCollected) {
      setIsLoading(false);
      return;
    }

    const preloadedResponse = takePreloadedDemographics(inviteId, locale);
    if (preloadedResponse) {
      setData(preloadedResponse);
      setError(null);
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        setIsLoading(true);
        const response = await InvitesHttpService.getDemographicsOptions(inviteId, locale);
        if (cancelled) return;
        setData(response);
        setError(null);
      } catch (e) {
        if (cancelled) return;
        console.error(`Error fetching demographics options: ${e}`);
        setError(e);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [inviteId, demographicsAlreadyCollected, locale]);

  return {
    questions: data?.questions ?? [],
    isLoading,
    error,
  };
}
